import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  Button,
  TextInput,
  ToastNotification,
  InlineNotification,
  Modal,
  SkeletonText,
} from '@carbon/react';
import { Add, Close, TrashCan } from '@carbon/icons-react';
import axiosInstance from '../utils/axiosInstance';
import PageContainer from '../components/PageContainer';
import { usePermissions } from '../contexts/PermissionContext';
import './KitchenAdmin.css';

const KitchenAdmin = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'g100';
  // Tipovi ishrane se dodaju/brišu samo uz dozvolu `diet-plans.manage`
  // (POST/DELETE /api/DietPlan). Bez nje ostaje samo pregled liste.
  const { can } = usePermissions();
  const canManageDietPlans = can('diet-plans.manage');

  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState(null);

  const [toast, setToast] = useState({ show: false, kind: 'success', title: '', subtitle: '' });

  const showToast = (kind, title, subtitle) => {
    setToast({ show: true, kind, title, subtitle });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 4000);
  };

  const fetchDietPlans = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/DietPlan');
      setDietPlans(response.data?.data || []);
    } catch (err) {
      console.error('Greška pri učitavanju tipova ishrane:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDietPlans(); }, []);

  const openPanel = () => {
    // Dodavanje tipa ishrane zahteva dozvolu `diet-plans.manage`.
    if (!canManageDietPlans) return;
    setName('');
    setFormError(null);
    setIsPanelOpen(true);
  };

  const closePanel = () => {
    setIsPanelOpen(false);
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('Naziv tipa ishrane je obavezan.');
      return;
    }
    try {
      setIsSaving(true);
      setFormError(null);
      await axiosInstance.post('/api/DietPlan', { name: name.trim() });
      closePanel();
      await fetchDietPlans();
      showToast('success', 'Uspešno!', `Tip ishrane "${name.trim()}" je dodat.`);
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0] || 'Greška pri dodavanju tipa ishrane.';
      setFormError(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const openDeleteModal = (plan) => {
    // Brisanje tipa ishrane zahteva dozvolu `diet-plans.manage`.
    if (!canManageDietPlans) return;
    setPlanToDelete(plan);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!planToDelete) return;
    try {
      await axiosInstance.delete(`/api/DietPlan/${planToDelete.id}`);
      setDietPlans(prev => prev.filter(p => p.id !== planToDelete.id));
      showToast('success', 'Uspešno!', `Tip ishrane "${planToDelete.name}" je obrisan.`);
    } catch (err) {
      showToast('error', 'Greška!', 'Nije moguće obrisati tip ishrane. Pokušajte ponovo.');
    } finally {
      setIsDeleteModalOpen(false);
      setPlanToDelete(null);
    }
  };

  return (
    <PageContainer>
      <div className={`kitchen-admin-page ${isDarkMode ? 'kitchen-admin-page--dark' : ''}`}>

        {/* Diet plan list with inline add row */}
        <div className="diet-plan-list">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="diet-plan-row diet-plan-row--skeleton">
                <SkeletonText width="40%" />
              </div>
            ))
          ) : dietPlans.length === 0 ? (
            <>
              <p className="diet-plan-empty">Nema dodanih tipova ishrane.</p>
              {canManageDietPlans && (
                <button className="diet-plan-add-row" onClick={openPanel}>
                  <Add size={16} />
                  <span>Dodaj novi tip ishrane</span>
                </button>
              )}
            </>
          ) : (
            <>
              {dietPlans.map(plan => (
                <div key={plan.id} className="diet-plan-row">
                  <span className="diet-plan-name">{plan.name}</span>
                  {canManageDietPlans && (
                    <Button
                      kind="danger--ghost"
                      size="sm"
                      renderIcon={TrashCan}
                      hasIconOnly
                      iconDescription="Obriši"
                      onClick={() => openDeleteModal(plan)}
                    />
                  )}
                </div>
              ))}
              {canManageDietPlans && (
                <button className="diet-plan-add-row" onClick={openPanel}>
                  <Add size={16} />
                  <span>Dodaj novi tip ishrane</span>
                </button>
              )}
            </>
          )}
        </div>

        {/* Right Side Panel */}
        <div className={`kitchen-admin-side-panel ${isPanelOpen && canManageDietPlans ? 'open' : ''}`}>
          <div className="side-panel-header">
            <h2>Novi tip ishrane</h2>
            <Button
              kind="ghost"
              size="sm"
              renderIcon={Close}
              hasIconOnly
              iconDescription="Zatvori"
              onClick={closePanel}
            />
          </div>

          {formError && (
            <div style={{ padding: '1rem 1.5rem 0' }}>
              <InlineNotification
                kind="error"
                title="Greška"
                subtitle={formError}
                onCloseButtonClick={() => setFormError(null)}
                hideCloseButton={false}
                lowContrast
              />
            </div>
          )}

          <div className="side-panel-content">
            <form onSubmit={handleSubmit} className="kitchen-admin-form">
              <TextInput
                id="diet-plan-name"
                labelText="Naziv tipa ishrane"
                placeholder="Unesite naziv"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSaving}
                required
              />

              <div className="form-actions">
                <Button
                  kind="secondary"
                  onClick={closePanel}
                  disabled={isSaving}
                >
                  Otkaži
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={isSaving}
                >
                  {isSaving ? 'Dodavanje...' : 'Dodaj'}
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* Overlay */}
        {isPanelOpen && canManageDietPlans && (
          <div
            className="side-panel-overlay"
            onClick={closePanel}
          />
        )}

        {/* Delete confirmation modal */}
        <Modal
          open={isDeleteModalOpen}
          onRequestClose={() => setIsDeleteModalOpen(false)}
          modalHeading="Brisanje tipa ishrane"
          primaryButtonText="Obriši"
          secondaryButtonText="Otkaži"
          onRequestSubmit={confirmDelete}
          danger
          size="sm"
        >
          <p>
            Da li ste sigurni da želite da obrišete tip ishrane{' '}
            <strong>{planToDelete?.name}</strong>?
          </p>
          <p style={{ marginTop: '1rem', fontSize: '0.875rem', opacity: 0.7 }}>
            Ova akcija se ne može poništiti.
          </p>
        </Modal>

        {/* Toast */}
        {toast.show && (
          <div className="toast-container">
            <ToastNotification
              kind={toast.kind}
              title={toast.title}
              subtitle={toast.subtitle}
              timeout={0}
              lowContrast
              onClose={() => setToast(t => ({ ...t, show: false }))}
            />
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default KitchenAdmin;
